"use client";

import { usePublicVbcActions } from '@to/public-vbc';
import { useCallback } from "react";
import { VendorDetailPage, VBCInquiryForm } from "@to/vendor";
import type {
  VendorDetailPageProps,
  RenderedTrustBadge,
  CategoryItem,
  ServiceArea,
  SpecialOfferItem,
  InquiryFormData,
} from "@to/vendor";

// Re-export types used by the server page for convenience
export type { RenderedTrustBadge, CategoryItem, ServiceArea, SpecialOfferItem };

export interface VendorDetailClientProps {
  vendor: VendorDetailPageProps["vendor"];
  vendorId: number;
  trustBadges: RenderedTrustBadge[];
  customBadges?: VendorDetailPageProps["customBadges"];
  categories: CategoryItem[];
  serviceAreas: ServiceArea[];
  offers: SpecialOfferItem[];
  backLink: { href: string; label: string };
  categoryBasePath: string;
  vcardContent?: string | null;
}

/**
 * Client wrapper that provides Agent-Directory-specific callbacks (contact-click
 * tracking, quote requests) to the shared VendorDetailPage component. Mirrors
 * OrgPortal's `VendorDetailClient`, minus what AD doesn't carry this phase:
 * no vCard QR (`vcardContent` omitted hides the QR section).
 *
 * The server page passes all serializable data here; this component wires up
 * the interactive behaviour via Agent Directory API routes.
 */
export function VendorDetailClient({
  vendor,
  vendorId,
  trustBadges,
  customBadges,
  categories,
  serviceAreas,
  offers,
  backLink,
  categoryBasePath,
  vcardContent,
}: VendorDetailClientProps) {
  const onAction=usePublicVbcActions(`/api/vendors/${vendorId}/actions`,vendorId);
  const onTrackInteraction = useCallback(
    (type: string, vid: string | number) => {
      // `keepalive: true` is REQUIRED — contact clicks usually navigate away
      // (tel:/mailto:/external site) and the POST must survive the unload
      // (2026-05-27 lesson).
      fetch(`/api/vendors/${vid}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type }),
        keepalive: true,
      }).catch(() => {});
    },
    [],
  );

  const onSubmitInquiry = useCallback(
    async (
      vid: string | number,
      formData: InquiryFormData,
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        // Body contract settled for the /api/vendor-quotes route (Task 36.3):
        // { vendorId: number, name: string, email: string, phone?: string,
        //   message: string }. JSON.stringify drops undefined optionals.
        const res = await fetch(`/api/vendor-quotes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vendorId: Number(vid),
            name: formData.senderName,
            email: formData.senderEmail,
            phone: formData.senderPhone,
            message: formData.message,
            operationId: formData.operationId,
            offerId: formData.offerId,
            intent: formData.intent,
          }),
        });

        if (res.status === 429) {
          return {
            success: false,
            error: "Too many requests. Please try again later.",
          };
        }

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          return {
            success: false,
            error: body.error || "Failed to send request. Please try again.",
          };
        }

        return { success: true };
      } catch {
        return {
          success: false,
          error: "Network error. Please check your connection and try again.",
        };
      }
    },
    [],
  );

  return (
    <VendorDetailPage
      key={vendorId}
      onAction={onAction}
      vendor={vendor}
      vendorId={vendorId}
      vcardContent={vcardContent}
      trustBadges={trustBadges}
      customBadges={customBadges}
      categories={categories}
      serviceAreas={serviceAreas}
      offers={offers}
      backLink={backLink}
      categoryBasePath={categoryBasePath}
      onTrackInteraction={onTrackInteraction}
      slots={{
        contactActionsSlot: (
          <VBCInquiryForm
            vendorId={vendorId}
            vendorName={vendor.companyName}
            onSubmitInquiry={onSubmitInquiry}
          />
        ),
      }}
    />
  );
}
